// ──────────────────────────────────────────────────────────────────────────
// briefBundles.js — configuration for the Intelligence Brief feature.
//
// Each bundle is a "lens" the user can pick from the dropdown. A bundle
// carries its own system prompt that is forwarded to Claude on the server
// via /api/brief. All prompts share a common scaffold (output format, source
// requirements, tone rules) and layer a domain focus on top.
//
// House style rules baked into every prompt:
//   - No em dashes anywhere.
//   - Paraphrase everything; single quotes under 15 words when unavoidable.
//   - 8 insights, primary sources, published in the past 7 days.
//   - Strict JSON output conforming to the schema the UI renders.
// ──────────────────────────────────────────────────────────────────────────

// Shared JSON shape for the model to fill. Kept in a single constant so the
// server prompt and the client parser stay in lockstep. The UI renders each
// field verbatim, so the schema also documents what gets drawn on screen.
export const BRIEF_JSON_SHAPE = `{
  "title": "string, like 'THE BRIEFING' or a bundle-tailored header",
  "edition": integer, today's edition number (the user passes this in),
  "date": "YYYY-MM-DD (today, as provided)",
  "bundle": "string, the bundle id the user selected",
  "insights": [
    {
      "number": "01" through "08",
      "section": "ALL CAPS SECTION NAME, short",
      "title": "one strong specific sentence, no em dashes",
      "body": "100 to 150 words. Dense factual content. Paraphrased. No em dashes.",
      "strategicRelevance": "why this matters to the reader in 1 to 3 sentences",
      "sources": [
        { "label": "Source name", "url": "https://..." }
      ]
    }
  ]
}`;

// Shared scaffold used by every bundle. We splice the bundle focus in where
// the {{FOCUS}} marker is. Kept as a function so today's date can be baked
// in at request time and the model treats it as authoritative.
//
// sourceWindow (optional): { startIso, endIso, reason } bounds the web_search
//   window. If provided, the model is told to ONLY surface stories first
//   published inside that window. The defaults (past 7 days) used to cause
//   heavy day-over-day overlap — today's brief scanning April 14-21, yesterday's
//   scanning April 13-20 meant six days of repeat ground. The window is now
//   computed by the client as max(firstOfMonth, previousBrief.generatedAt).
// previousInsights (optional): array of { title, sources } from the prior
//   edition for this bundle. We list them in the prompt so the model knows
//   which ground is already covered and avoids re-reporting the same story
//   with a slightly different framing.
export function buildSystemPrompt({
  bundleId, focusPrompt, today, edition, bundleName,
  sourceWindow, previousInsights,
}) {
  const windowBlock = sourceWindow
    ? `SOURCE DATE WINDOW (hard boundary):
- Only include stories whose PRIMARY REPORT was first published between ${sourceWindow.startIso} and ${sourceWindow.endIso}.
- Do not include anything older than ${sourceWindow.startIso}, even if it is still circulating.
- Ideal weighting: most recent first. Prefer last 24 to 72 hours where possible.
- If fewer than eight qualifying stories exist inside the window, return only the qualifying ones. Do not pad with older material to hit eight.
- Reason for window: ${sourceWindow.reason || 'avoid overlap with prior editions'}.`
    : `SOURCE DATE WINDOW:
- Use the past 7 days. Prefer same-day and last 72 hours.`;

  const priorBlock = Array.isArray(previousInsights) && previousInsights.length
    ? `ALREADY COVERED IN PRIOR EDITION (do not re-report these unless there is a substantively NEW development in the window above):
${previousInsights.slice(0, 10).map((ins, i) => {
      const src = Array.isArray(ins.sources) && ins.sources.length
        ? ` [${ins.sources.slice(0, 2).map(s => s?.url).filter(Boolean).join(', ')}]`
        : '';
      return `  ${i + 1}. ${ins.title || '(untitled)'}${src}`;
    }).join('\n')}
If a story above has moved forward with concrete new facts inside the window (new filing, new number, new statement, new ruling), you MAY cover it as a follow-up, but frame the insight around the NEW development, not the old one.`
    : '';

  return `You are the editorial brain behind an intelligence briefing that shows up daily for an ambitious, intelligent reader. Today is ${today}. This is edition ${edition}. The bundle the reader selected is: ${bundleName} (id: ${bundleId}).

FOCUS FOR THIS BUNDLE:
${focusPrompt}

${windowBlock}

${priorBlock}

YOUR JOB:
1. Use the web_search tool to find up to eight (8) high-leverage stories inside the window above. Prefer primary sources: company blogs, first-party announcements, original reporting, academic papers, government and court filings, regulator releases, conference videos, preprint servers. De-prioritize aggregators (Google News roll-ups, Reddit, Twitter threads without a primary link, social recaps).
2. For each story, write a dense, briefing-style insight. The reader is sophisticated; do not waste words explaining context they already have.
3. Return the result as a single JSON object that matches the schema below. No preamble, no markdown, no backticks, just the JSON object.

EVERY INSIGHT MUST HAVE:
- number: "01" through "08", in order
- section: a short ALL CAPS category label, e.g. "AI INFRA", "CHINA", "BIOTECH"
- title: one strong specific sentence, no em dashes
- body: 100 to 150 words, paraphrased, no em dashes. Dense facts, no filler.
- strategicRelevance: 1 to 3 sentences explaining why the reader should care
- sources: 2 to 4 items. Each item is { "label": string, "url": string }. URLs must be real primary links you actually looked at via web_search. Do not fabricate URLs.

HARD RULES (non-negotiable):
- Never use em dashes anywhere. Replace with commas, periods, colons, or restructure.
- Never quote any source beyond 15 words. Paraphrase otherwise.
- Do not repeat the same story under different framings.
- Do not fabricate URLs, quotes, numbers, or names. If you cannot verify a detail, omit it.
- Do not include headlines, intro paragraphs, or summary commentary outside the JSON.
- Do not pad word counts with filler. Density over length.
- Honor the SOURCE DATE WINDOW absolutely. Returning fewer than eight insights is fine. Surfacing stories from before the window is not.

OUTPUT FORMAT (return EXACTLY this JSON object, nothing else):
${BRIEF_JSON_SHAPE}
`;
}

// Bundle registry. The UI reads this to render the dropdown. `accent` is a
// hex used for the bundle chip in the Intelligence cover. `description` is
// the one-liner shown under the bundle name in the picker.
//
// Named for the industry the reader works in. No "Full Spectrum"; if someone
// wants a grab-bag they can write a Custom prompt.
export const BUNDLES = [
  {
    id: 'technology',
    name: 'Technology',
    description: 'AI, software, startups, consumer tech, platform shifts',
    accent: '#2D9CFF',
    focus: `You cover the technology industry. Domains to hunt across:
- AI tooling, model releases, enterprise AI adoption, model pricing, inference infra
- Consumer tech and social commerce: TikTok Shop, Instagram Shopping, YouTube Shopping, Shopify, Amazon changes
- Creator economy: platform monetization shifts, creator tooling, ad pricing
- Startups and venture: funding rounds that matter, acquisitions, strategic pivots at major tech companies
- Developer tooling, open source releases, security disclosures
Weight toward moves that change how operators should build, sell, or distribute this quarter. Skip pure stock price commentary.`,
  },
  {
    id: 'finance',
    name: 'Finance',
    description: 'Markets, macro, fintech, M&A, regulation',
    accent: '#47A6FF',
    focus: `You cover the finance industry. Domains to hunt across:
- Central bank decisions, macro data prints, rate and FX moves that actually matter
- M&A, IPOs, private equity deals of consequence
- Regulatory actions from the SEC, CFTC, FINRA, Fed, BoE, ECB, BIS
- Fintech and payments: new rails, stablecoin regulation, card network moves
- Credit markets, defaults, distressed debt, restructurings
- Hedge fund and asset manager positioning when filings or principals disclose
Prefer primary filings and official statements over analyst notes. Skip pure stock price commentary and retail speculation.`,
  },
  {
    id: 'science',
    name: 'Science',
    description: 'Research breakthroughs, health, biotech, physics',
    accent: '#7FE1A0',
    focus: `You cover research at the frontier. Domains to hunt across:
- Longevity, aging biology, senescence research, metabolic health
- Clinical trial readouts, FDA and EMA decisions, approvals and rejections
- Neuroscience, CNS disorders, sleep, brain-computer interfaces
- Physics (quantum, fusion, materials), biotech, gene therapy, CRISPR, protein design
- Preprints on bioRxiv, medRxiv, arXiv that represent real steps, not hype
Prefer primary papers and press releases from the institutions themselves. Call out sample sizes, effect sizes, and limitations where relevant. Skip sensational health blogs.`,
  },
  {
    id: 'politics',
    name: 'Politics',
    description: 'Geopolitics, governance, conflicts, policy',
    accent: '#C77DFF',
    focus: `You cover the exercise of power. Domains to hunt across:
- Active conflicts: frontlines, diplomacy, arms transfers, ceasefire dynamics
- Great power competition: US, China, Russia, EU, India moves
- Sanctions, export controls, trade policy changes
- Elections, government formations, major legislation in G20 countries
- Intelligence, defense, and security service activity that surfaces publicly
- Corruption and accountability cases involving heads of state, ministers, CEOs of listed firms
Prefer Reuters, AP, FT, WSJ, AFP primary reporting, government statements, court filings, and think-tank primary analysis. Skip opinion unless it is from a named principal. Be descriptive, not partisan.`,
  },
  {
    id: 'culture',
    name: 'Culture',
    description: 'Media, entertainment, sports, social trends',
    accent: '#FF7DA8',
    focus: `You cover media, entertainment, and cultural trends. Domains to hunt across:
- Film, TV, streaming: box office, major release decisions, platform strategy shifts
- Music industry moves: label news, major releases, touring and live economics
- Gaming: major releases, studio news, platform shifts, esports
- Sports business: league decisions, major trades, media rights
- Publishing, books, long-form media, newsletter and podcast economics
- Notable cultural moments breaking into mainstream discourse
Prefer trade publications and primary announcements over gossip. Skip celebrity rumors.`,
  },
];

// Build the runtime prompt for a given bundle. For the custom variant we
// accept an extra free-text focus the user has typed, and produce a prompt
// that slots it into the same scaffold so structure stays constant.
//
// sourceWindow and previousInsights are optional. When the client passes them
// in (the Intelligence scene looks up the latest prior brief for this bundle
// before calling the API), they tighten the web_search window and tell the
// model which stories are already covered. Without them, the prompt falls
// back to the original "past 7 days" default.
export function promptForBundle({
  bundleId, customFocus, today, edition,
  sourceWindow, previousInsights,
}) {
  if (bundleId === 'custom') {
    const focus = (customFocus || '').trim() || 'A custom intelligence brief for the reader. Be unopinionated about scope, lean on primary sources, keep density high.';
    return buildSystemPrompt({
      bundleId: 'custom',
      bundleName: 'Custom',
      focusPrompt: focus,
      today,
      edition,
      sourceWindow,
      previousInsights,
    });
  }
  const b = BUNDLES.find(x => x.id === bundleId) || BUNDLES.find(x => x.id === 'technology') || BUNDLES[0];
  return buildSystemPrompt({
    bundleId: b.id,
    bundleName: b.name,
    focusPrompt: b.focus,
    today,
    edition,
    sourceWindow,
    previousInsights,
  });
}

// Map from bundle id to display metadata (name, description, accent).
// The scene uses this to render the dropdown and bundle chips without
// hard-coding strings outside this module.
export function bundleMeta(bundleId) {
  if (bundleId === 'custom') {
    return {
      id: 'custom',
      name: 'Custom',
      description: 'Your own topic focus',
      accent: '#41B3FF',
    };
  }
  return BUNDLES.find(b => b.id === bundleId) || BUNDLES[0];
}
